import { useEffect, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Field, NativeSelect } from "@/components/common";
import { SERVICE_LINES } from "@/lib/serviceLines";

export const JOB_STATUSES = ["created", "scheduled", "prepress", "in_production", "finishing", "qa", "ready", "dispatched", "closed"];
const KEY = "pressops.jobs.filters";
const BLANK = { q: "", status: "", service_line: "", priority: "", branch_id: "", due_from: "", due_to: "" };
const label = (s) => s.replace(/_/g, " ").replace(/^qa$/, "QA").replace(/^\w/, (c) => c.toUpperCase());

export function useJobFilters() {
  const [f, setF] = useState(() => { try { return { ...BLANK, ...JSON.parse(sessionStorage.getItem(KEY) || "{}") }; } catch { return BLANK; } });
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(f)); }, [f]);
  return [f, setF, () => setF(BLANK)];
}

export const activeCount = (f) => Object.entries(f).filter(([k, v]) => k !== "q" && v).length;

export function applyJobFilters(rows, f) {
  const q = f.q.trim().toLowerCase();
  return rows.filter((j) => (!f.status || (j.status || "created") === f.status) && (!f.service_line || j.service_line === f.service_line)
    && (!f.priority || (j.priority || "normal") === f.priority) && (!f.branch_id || j.branch_id === f.branch_id)
    && (!f.due_from || (j.due_date || "").slice(0, 10) >= f.due_from) && (!f.due_to || ((j.due_date || "").slice(0, 10) && j.due_date.slice(0, 10) <= f.due_to))
    && (!q || [j.number, j.title, j.customer_name].some((v) => (v || "").toLowerCase().includes(q))));
}

function Fields({ f, setF, branches, idp }) {
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  return <>
    <Field label="Status"><NativeSelect testId={`${idp}-status`} value={f.status} onChange={set("status")} options={[{ value: "", label: "All statuses" }, ...JOB_STATUSES.map((s) => ({ value: s, label: label(s) }))]} /></Field>
    <Field label="Service line"><NativeSelect testId={`${idp}-service-line`} value={f.service_line} onChange={set("service_line")} options={[{ value: "", label: "All lines" }, ...SERVICE_LINES]} /></Field>
    <Field label="Priority"><NativeSelect testId={`${idp}-priority`} value={f.priority} onChange={set("priority")} options={[{ value: "", label: "Any priority" }, ...["urgent", "high", "normal", "low"].map((p) => ({ value: p, label: label(p) }))]} /></Field>
    {branches.length > 1 && <Field label="Branch"><NativeSelect testId={`${idp}-branch`} value={f.branch_id} onChange={set("branch_id")} options={[{ value: "", label: "All branches" }, ...branches.map((b) => ({ value: b.id, label: b.name }))]} /></Field>}
    <Field label="Due from"><Input type="date" className="h-9 min-w-0" data-testid={`${idp}-due-from`} value={f.due_from} onChange={(e) => set("due_from")(e.target.value)} /></Field>
    <Field label="Due to"><Input type="date" className="h-9 min-w-0" data-testid={`${idp}-due-to`} value={f.due_to} onChange={(e) => set("due_to")(e.target.value)} /></Field>
  </>;
}

export function JobFilters({ f, setF, reset, branches, shown, total }) {
  const [open, setOpen] = useState(false);
  const n = activeCount(f);
  return (
    <div className="mb-4 space-y-3" data-testid="jobs-filter-bar">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="jobs-search-input" className="h-10 pl-9" placeholder="Search job number, title or customer" value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} />
        </div>
        <Button variant="outline" className="h-10 md:hidden" data-testid="jobs-filter-open" onClick={() => setOpen(true)}><SlidersHorizontal className="mr-1 h-4 w-4" />Filters{n ? <span className="ml-1.5 rounded-full bg-[#13294B] px-1.5 text-[11px] text-white" data-testid="jobs-filter-count">{n}</span> : null}</Button>
        {(n > 0 || f.q) && <Button variant="ghost" className="h-10 shrink-0" data-testid="jobs-filter-reset" onClick={reset}><X className="mr-1 h-4 w-4" />Reset</Button>}
      </div>
      <div className="po-card hidden gap-3 p-4 md:grid md:grid-cols-3 xl:grid-cols-6" data-testid="jobs-filter-inline"><Fields f={f} setF={setF} branches={branches} idp="jobs-filter" /></div>
      <p className="text-xs text-slate-500" data-testid="jobs-filter-summary">Showing {shown} of {total} jobs</p>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl" data-testid="jobs-filter-sheet">
          <SheetHeader><SheetTitle>Filter jobs</SheetTitle><SheetDescription>Filters combine and stay set for this session.</SheetDescription></SheetHeader>
          <div className="mt-4 grid grid-cols-1 gap-3 min-[420px]:grid-cols-2"><Fields f={f} setF={setF} branches={branches} idp="jobs-sheet" /></div>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-11" data-testid="jobs-sheet-reset" onClick={reset}>Reset</Button>
            <Button className="h-11" data-testid="jobs-sheet-apply" onClick={() => setOpen(false)}>Show {shown} jobs</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
